/**
 * Shared parsing for the `Content-Disposition` response header, used to
 * derive a download file name. Handles both the plain `filename="..."` form
 * and the RFC 5987 `filename*=UTF-8''...` percent-encoded form, including
 * the MIME encoded-word (`=?utf-8?q?...?=`) variant some backends send
 * inside the RFC 5987 value.
 */

const decodeFileName = (value: string): string => {
  const trimmedValue = value.trim().replace(/^"|"$/g, "");

  try {
    if (/^=\?utf-8\?q\?/i.test(trimmedValue)) {
      const encodedValue = trimmedValue
        .replace(/^=\?utf-8\?q\?/i, "")
        .replace(/\?=$/, "")
        .replace(/_/g, " ")
        .replace(/=([0-9a-f]{2})/gi, "%$1");
      return decodeURIComponent(encodedValue);
    }

    return decodeURIComponent(trimmedValue);
  } catch {
    return trimmedValue;
  }
};

/**
 * Extracts a file name from a `Content-Disposition` header value, preferring
 * the RFC 5987 `filename*=UTF-8''...` form when present, falling back to the
 * plain `filename="..."` form, and finally to `fallbackFileName`.
 */
export const getFileNameFromContentDisposition = (
  contentDisposition: string | undefined,
  fallbackFileName: string
): string => {
  if (!contentDisposition) return fallbackFileName;

  const utf8FileName = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i
  )?.[1];
  if (utf8FileName) return decodeFileName(utf8FileName);

  const regularFileName = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i
  );
  if (!regularFileName) return fallbackFileName;

  return decodeFileName(regularFileName[1] || regularFileName[2]);
};
